<script lang="ts" setup generic="T extends Nullable<string | number> | undefined">
  import { VueTelInput } from 'vue-tel-input'

  import type { Nullable } from '~/core/types'

  type Props = {
    disabled?: boolean
    required?: boolean
    light?: boolean
    name: string
    errors?: string[]
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    required: false,
  })
  const { t } = useI18n()

  const model = defineModel<T>({ required: true, default: null })

  const telInputOpen = ref(false)

</script>

<template>
  <div
    class="c-input c-phone-input"
    :class="{'light':props.light}"
  >
    <label for="input-tel">{{ t('common.phone') }}</label>
    <VueTelInput
      v-model="model"
      default-country="FR"
      :preferred-countries="['FR', 'BE', 'DE', 'ES', 'NL', 'GB']"
      mode="international"
      :input-options="{
        placeholder: t('common.phone') ,
        id: props.name,
        required: props.required
      }"
      :dropdown-options="{ width: '100%', showFlags: true }"
      @open="telInputOpen = true"
      @close="telInputOpen = false"
    >
      <template #arrow-icon>
        <span />
      </template>
    </VueTelInput>
    <InputBase
      v-model="model"
      :name="props.name"
      type="hidden"
      :required="props.required"
    />
    <InputErrors :errors="props.errors" />
  </div>
</template>

<style scoped>

.c-phone-input {
  position: relative;
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 0.5rem;
    color :var(--color-secondary);
    font-family: Open-sans, sans-serif;
    font-size: 0.875em;
    opacity: 0.6;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  &:deep(.vue-tel-input) {
    position: relative;
    border: none;
    box-shadow: none;

    .vti__dropdown-list {
      z-index: 10;
      top: -204px;
      left: 0;
      width: 100%;
      border: none;
      border-radius: 0.25rem;
      background-color: white;
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size :inherit;
      font-weight: normal;
    }

    .vti__dropdown-item {
      transition: background-color ease-out .1s;
    }

    .vti__dropdown-item.highlighted {
      background-color: white;
    }

    .vti__dropdown {
      position: static;
      border-radius: 0.25rem 0 0 0.25rem;
      border-right: solid 1px var(--color-shadow-2);
      background-color: var(--color-secondary-light);
    }

    .vti__input {
      padding: 0.625rem;
      border: none;
      border-radius: 0 0.25rem 0.25rem 0;
      background-color: var(--color-secondary-light);
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1.125em;
      font-weight: 600;
      line-height: 1em;
      opacity: 1;
      outline: none;
      transition: all ease-out 0.15s;
    }

  }

  &.light {
    &:deep(.vue-tel-input) {
      .vti__input {
        background-color: white;
      }

      .vti__dropdown {
        background-color:white;
      }

    }
  }

  .error {
    display: flex;
    margin-top: 0.25em;
    color: var(--color-error);
    font-family: Open-Sans, sans-serif;
    font-size: 0.75em;
    font-weight: normal;
  }
}
</style>
